import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import PanelCard from '../../performance-operations/components/PanelCard';
import { Clickable, Sub, Table } from '../../performance-operations/styles';
import { formatNumber } from '../../performance-operations/config';
import {
  AxisX, AxisY, Cell, CellName, CellValue, Matrix,
} from '../styles';
import { PERF_COLS, POT_ROWS, axisLabel, cellOf } from '../config';

// Analiz E09 orta alan — X performans, Y potansiyel 3×3 matris; hücrede sayı ve yüzde. Matris n = hücre toplamı; eksik eksenli
// kişiler matris dışında (yan panel). Küçük grup gizli hücre tıklanmaz ve değeri gösterilmez. Potansiyel erişimi yoksa yalnız
// performans bantları (3 sütun). Hücre tıklaması alttaki listeyi süzer; tablo görünümü klavyeyle aynı detaya iner.
function CellBody({ t, lang, cell, potentialVisible }) {
  return (
    <>
      <CellName title={axisLabel(t, cell.key, potentialVisible)}>{axisLabel(t, cell.key, potentialVisible)}</CellName>
      {cell.suppressed
        ? <Sub data-suppressed="1">{t('talent.nineBoxTransitions.suppressedCell')}</Sub>
        : (
          <>
            <CellValue>{formatNumber(cell.count, lang, 0)}</CellValue>
            <Sub>{cell.pct == null ? '-' : `${formatNumber(cell.pct, lang)}%`}</Sub>
          </>
        )}
    </>
  );
}

export default function NineBoxMatrixPanel({ panel, selection, onSelect }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [table, setTable] = useState(false);
  const data = panel.data;
  const cells = data?.cells || [];
  const visible = data?.potentialVisible !== false;
  const byKey = Object.fromEntries(cells.map((c) => [c.key, c]));
  const max = Math.max(1, ...cells.map((c) => c.count || 0));
  const selectedCell = selection?.type === 'cell' ? selection.cell : null;
  const pick = (cell) => { if (!cell.suppressed) onSelect({ type: 'cell', cell: cell.key }); };

  const renderCell = (key) => {
    const cell = byKey[key];
    if (!cell) return <div key={key} />;
    return (
      <Cell
        key={key} type="button" data-cell={key} disabled={cell.suppressed} $selected={selectedCell === key}
        $tone={cell.suppressed ? 0 : (cell.count || 0) / max}
        aria-label={`${axisLabel(t, key, visible)}: ${cell.suppressed ? t('talent.nineBoxTransitions.suppressedCell') : cell.count}`}
        onClick={() => pick(cell)}
      >
        <CellBody t={t} lang={lang} cell={cell} potentialVisible={visible} />
      </Cell>
    );
  };

  return (
    <PanelCard
      panelKey="matrix" title={t('talent.nineBoxTransitions.panel.matrix')} span={8}
      loading={panel.loading} error={panel.error} onRetry={panel.retry}
      empty={!!data && !data.process} emptyText={t('talent.nineBoxTransitions.noProcess')}
      tableToggle showTable={table} onToggleTable={() => setTable((v) => !v)}
      actions={data ? <Sub data-meta="matrix-n">{t('talent.nineBoxTransitions.matrixN', { n: data.placedN, expected: data.expectedN })}</Sub> : null}
    >
      {table ? (
        <Table>
          <thead>
            <tr>
              <th>{t('talent.nineBoxTransitions.col.cell')}</th>
              <th>{t('talent.nineBoxTransitions.col.count')}</th>
              <th>{t('talent.nineBoxTransitions.col.pct')}</th>
            </tr>
          </thead>
          <tbody>
            {cells.map((c) => (
              <tr key={c.key} data-row={c.key}>
                <td>
                  {c.suppressed ? axisLabel(t, c.key, visible) : (
                    <Clickable type="button" data-pick={c.key} onClick={() => pick(c)}>{axisLabel(t, c.key, visible)}</Clickable>
                  )}
                </td>
                <td>{c.suppressed ? t('talent.nineBoxTransitions.suppressedCell') : formatNumber(c.count, lang, 0)}</td>
                <td>{c.suppressed || c.pct == null ? '-' : `${formatNumber(c.pct, lang)}%`}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <Matrix $cols={3}>
          {visible ? POT_ROWS.map((pot) => (
            <React.Fragment key={pot}>
              <AxisY>{`${t('performance.ninebox.potential')} — ${t(`performance.ninebox.${pot}`)}`}</AxisY>
              {PERF_COLS.map((perf) => renderCell(cellOf(pot, perf)))}
            </React.Fragment>
          )) : (
            <>
              <AxisY>{t('performance.ninebox.performance')}</AxisY>
              {PERF_COLS.map((perf) => renderCell(perf))}
            </>
          )}
          <div />
          {PERF_COLS.map((perf) => (
            <AxisX key={perf}>{`${t('performance.ninebox.performance')} — ${t(`performance.ninebox.${perf}`)}`}</AxisX>
          ))}
        </Matrix>
      )}
      {!visible ? <Sub data-note="performance-only">{t('talent.nineBoxTransitions.performanceOnlyNote')}</Sub> : null}
    </PanelCard>
  );
}
